import * as React from 'react';
import { Button, Card, CardContent, DropdownSelect, ErrorState, Input, PageHeader, SkeletonCard } from '@/components/ui';
import { ApiError } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import {
  defaultReportRange,
  fetchReport,
  validateReportRange,
  type AgentPerformanceReport,
  type CsatReport,
  type ReportDataByKind,
  type ReportKind,
  type RcaReport,
  type SlaReport,
  type VolumeReport,
} from '@/lib/reports-api';
import { ReportView } from '@/components/reports/report-sections';

type Translator = ReturnType<typeof useI18n>['t'];

function reportError(error: unknown, t: Translator): string {
  return error instanceof ApiError ? error.message : t('dir.reports.loadError');
}

/** validateReportRange (lib) returns English; map its known messages to the catalog. */
function localizeRangeError(message: string | null, t: Translator): string | null {
  if (!message) return message;
  const known: Record<string, Parameters<Translator>[0]> = {
    'Choose valid start and end dates.': 'dir.reports.rangeInvalid',
    'The end date must be on or after the start date.': 'dir.reports.rangeOrder',
    'Reports support a maximum date range of 90 days.': 'dir.reports.rangeMax',
  };
  return known[message] ? t(known[message]) : message;
}

function reportOptions(t: Translator) {
  return [
    { value: 'volume', label: t('dir.reports.volume') },
    { value: 'sla', label: t('dir.reports.sla') },
    { value: 'agents', label: t('dir.reports.agents') },
    { value: 'csat', label: t('dir.reports.csat') },
    { value: 'rca', label: t('dir.reports.rca') },
  ];
}

function reportDescription(kind: ReportKind, t: Translator): string {
  return {
    volume: t('dir.reports.descVolume'),
    sla: t('dir.reports.descSla'),
    agents: t('dir.reports.descAgents'),
    csat: t('dir.reports.descCsat'),
    rca: t('dir.reports.descRca'),
  }[kind];
}

export function ReportsPage() {
  const { t } = useI18n();
  const initialRange = React.useMemo(defaultReportRange, []);
  const [from, setFrom] = React.useState(initialRange.from);
  const [to, setTo] = React.useState(initialRange.to);
  const [report, setReport] = React.useState<ReportKind>('volume');
  const [data, setData] = React.useState<ReportDataByKind[ReportKind] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const rangeError = localizeRangeError(validateReportRange({ from, to }), t);

  const load = React.useCallback(async (signal?: AbortSignal) => {
    const validation = validateReportRange({ from, to });
    if (validation) {
      setError(localizeRangeError(validation, t) ?? validation);
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await fetchReport(report, { from, to }, signal);
      if (!signal?.aborted) setData(result);
    } catch (caught) {
      if (caught instanceof Error && caught.name === 'AbortError') return;
      if (!signal?.aborted) setError(reportError(caught, t));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [from, report, to, t]);

  React.useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  function renderReport() {
    if (!data) return <Card><CardContent className="p-0"><div className="py-12 text-center text-sm text-ink-subtle">{t('dir.reports.noPayload')}</div></CardContent></Card>;
    if (report === 'volume') return <ReportView kind="volume" data={data as VolumeReport} />;
    if (report === 'sla') return <ReportView kind="sla" data={data as SlaReport} />;
    if (report === 'agents') return <ReportView kind="agents" data={data as AgentPerformanceReport} />;
    if (report === 'csat') return <ReportView kind="csat" data={data as CsatReport} />;
    return <ReportView kind="rca" data={data as RcaReport} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader className="mb-0" title={t('nav.reports')} description={reportDescription(report, t)} actions={<Button variant="outline" onClick={() => void load()} disabled={loading}>{t('dir.reports.refresh')}</Button>} />
      <Card className="overflow-visible border-brand-100 bg-brand-50/40">
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="w-full sm:w-56"><DropdownSelect label={t('dir.reports.report')} options={reportOptions(t)} value={report} onChange={(value) => setReport(value as ReportKind)} /></div>
          <div className="w-full sm:w-40"><Input label={t('dir.reports.from')} type="date" value={from} onChange={(event) => setFrom(event.target.value)} error={rangeError || undefined} /></div>
          <div className="w-full sm:w-40"><Input label={t('dir.reports.to')} type="date" value={to} onChange={(event) => setTo(event.target.value)} error={rangeError || undefined} /></div>
          <p className="w-full text-xs text-ink-subtle sm:w-auto sm:pb-2">{t('dir.reports.maxRange')}</p>
        </CardContent>
      </Card>
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : loading ? <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></div> : renderReport()}
    </div>
  );
}

export default ReportsPage;
